import { Bot, ExternalLink } from "lucide-react";
import { useGetOne } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { ContactConversationTimeline } from "../contacts/ContactConversationTimeline";
import { ContactEchoesPanel } from "../echoflicks/EchoList";
import type { Contact, WhatsAppBroadcastRecipient } from "../types";
import {
  broadcastStageLabel,
  deriveRecipientStage,
  isLikelyAutoResponder,
  recipientDisplayName,
  recipientFlag,
} from "./broadcastBoard";

function formatDateTime(value?: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

/** A minimal Contact for the reused conversation/echoes panels (keyed on id). */
function asContact(recipient: WhatsAppBroadcastRecipient): Contact {
  return {
    id: recipient.contact_id ?? recipient.id,
    first_name: recipientDisplayName(recipient),
    last_name: "",
    whatsapp_user_id: recipient.whatsapp_user_id,
    phone_e164: recipient.phone_country_calling_code
      ? `+${recipient.phone_country_calling_code}`
      : undefined,
  } as unknown as Contact;
}

export function BroadcastContactQuickViewModal({
  recipient,
  onClose,
}: {
  recipient: WhatsAppBroadcastRecipient | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={recipient != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {recipient ? (
          <ModalBody recipient={recipient} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const MILESTONES: { label: string; field: keyof WhatsAppBroadcastRecipient }[] =
  [
    { label: "Sent", field: "first_sent_at" },
    { label: "Delivered", field: "first_delivered_at" },
    { label: "Read", field: "first_read_at" },
    { label: "Replied", field: "first_inbound_after_broadcast_at" },
    { label: "/create", field: "first_create_command_at" },
    { label: "/buy", field: "first_buy_command_at" },
    { label: "Echo started", field: "first_echo_started_at" },
    { label: "Final delivered", field: "first_final_video_delivered_at" },
  ];

function ModalBody({
  recipient,
  onClose,
}: {
  recipient: WhatsAppBroadcastRecipient;
  onClose: () => void;
}) {
  const { key: stage } = deriveRecipientStage(recipient);
  const flag = recipientFlag(recipient.phone_country_iso2);
  const reply = recipient.reply_text?.trim();
  const autoReply = isLikelyAutoResponder(reply);
  const contact = asContact(recipient);

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {flag ? `${flag} ` : ""}
          {recipientDisplayName(recipient)}
        </DialogTitle>
        <DialogDescription asChild>
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {recipient.whatsapp_user_id ? (
              <span className="tabular-nums">{recipient.whatsapp_user_id}</span>
            ) : null}
            {recipient.phone_country_name ? (
              <span>{recipient.phone_country_name}</span>
            ) : null}
            <Badge variant="outline" className="font-normal">
              {broadcastStageLabel(stage)}
            </Badge>
            {recipient.attribution_confidence ? (
              <span className="capitalize">
                {recipient.attribution_confidence} confidence
              </span>
            ) : null}
          </div>
        </DialogDescription>
      </DialogHeader>

      {reply ? (
        <div className="rounded-md border bg-muted/40 p-3">
          <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
            First reply
            {autoReply ? (
              <Badge variant="outline" className="gap-1 font-normal">
                <Bot className="size-3" aria-label="Auto-reply" />
                likely auto-reply
              </Badge>
            ) : null}
          </div>
          <p className="text-sm">{reply}</p>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {MILESTONES.map((milestone) => {
          const value = recipient[milestone.field] as string | null | undefined;
          return (
            <div
              key={milestone.label}
              className={`rounded-md border p-2 ${value ? "" : "opacity-50"}`}
            >
              <div className="text-[11px] text-muted-foreground">
                {milestone.label}
              </div>
              <div className="text-xs tabular-nums">
                {formatDateTime(value)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Echoes &amp; credits</h3>
          <BroadcastContactCreditSummary contactId={contact.id} />
        </div>
        <ContactEchoesPanel contact={contact} />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Conversation</h3>
        <ContactConversationTimeline
          contact={contact}
          heightClassName="h-[420px]"
        />
      </div>

      <div className="flex justify-end">
        <Button asChild variant="outline" size="sm" onClick={onClose}>
          <Link to={`/contacts/${contact.id}/show`}>
            Open full profile
            <ExternalLink className="size-4" />
          </Link>
        </Button>
      </div>
    </>
  );
}

/**
 * Live credit balance + lifetime Echo count, fetched from the contact profile.
 * `credit_balance` is the balance the bot last reported on a purchase / token
 * grant (incl. the free-credit auto-grant bypass); it is absent until a contact
 * has had a grant event, so we render "—" rather than a misleading 0.
 */
function BroadcastContactCreditSummary({
  contactId,
}: {
  contactId: Contact["id"];
}) {
  const { data, isPending, error } = useGetOne<Contact>(
    "contacts",
    { id: contactId },
    { enabled: contactId != null },
  );

  const credits =
    typeof data?.credit_balance === "number" ? data.credit_balance : null;
  const echoes =
    typeof data?.video_count === "number" ? data.video_count : null;

  const render = (value: number | null) => {
    if (isPending) return "…";
    if (error) return "—";
    return value == null ? "—" : value.toLocaleString();
  };

  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <CreditStat label="Credits" value={render(credits)} />
      <CreditStat label="Echoes made" value={render(echoes)} />
    </div>
  );
}

function CreditStat({ label, value }: { label: string; value: string }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md border px-2 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-semibold tabular-nums">{value}</span>
    </span>
  );
}
